<template>
	<div class="table-overflow-hide">
		<div class="directoryTable scrollable-55vh">
			<table class="table">
				<thead class="sticky-header">
					<tr>
						<th class="no-col-header">No.</th>
						<th v-for="(header, i) in headers" :key="header.header + i">
							<div tabindex="1" class="d-flex align-center gap-2 justify-center w-100">
								<span style="text-transform: none">
									{{ header.header }}
								</span>
								<v-tooltip v-if="header.message" activator="parent" location="bottom">{{
									header.message
								}}</v-tooltip>
								<v-icon
									size="small"
									:color="header.type == 'warning' ? '#ff9200' : 'rgb(255, 0, 0)'"
									v-if="!header.valid"
								>
									{{ header.type == "warning" ? "mdi-alert" : "mdi-close-circle" }}
								</v-icon>
							</div>
						</th>
						<th v-for="i in Math.max(0, maxCols - headers.length)" :key="i">
							<div tabindex="1" class="d-flex align-center gap-2 justify-center w-100">
								<v-tooltip activator="parent" location="bottom"
									>Number of cols exceeding number of headers</v-tooltip
								>
								<v-icon size="small" color="rgb(255, 0, 0)">mdi-close-circle</v-icon>
							</div>
						</th>
					</tr>
				</thead>
				<tbody>
					<tr v-for="(row, i) in rows" :key="i">
						<td class="no-col">
							{{ i + 1 }}
						</td>
						<td v-for="(col, i) in row" :key="i" style="text-align: center">
							{{ col }}
						</td>
						<td v-for="i in Math.max(0, maxCols - row.length)" :key="i">
							<div tabindex="1" class="d-flex align-center gap-2 justify-center w-100">
								<v-tooltip activator="parent" location="bottom"
									>Missing Field Value</v-tooltip
								>
								<v-icon size="small" color="warning"> mdi-alert </v-icon>
							</div>
						</td>
					</tr>
				</tbody>
			</table>
		</div>
	</div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import type { TCsvType } from "./utils";
import { parse } from "papaparse";
import { sort } from "@/utils/sort";

const props = defineProps<{
	fileStr: string;
	csvType: TCsvType;
}>();

const parsed = computed(() => parse(props.fileStr.trimEnd()).data as string[][]);

const headers = computed(() => {
	if (!props.fileStr) return [];
	const headers = parsed.value[0];
	return headers.map((h, i) => {
		if (!props.csvType.fields.includes(h))
			return {
				header: h,
				valid: false,
				message: h ? "Field not supported" : "Unsupported comma at end of line",
				type: h ? "warning" : "error",
			} as const;
		const correctIndex = props.csvType.fields.indexOf(h);
		if (i !== correctIndex)
			return {
				header: h,
				valid: false,
				message: `field out of order, the correct position is ${correctIndex + 1}`,
				type: "error",
			} as const;
		return { header: h, valid: true, message: "", type: "" } as const;
	});
});
const rows = computed(() => sort(parsed.value.slice(1), (i) => i.length));

const maxCols = computed(() =>
	parsed.value.reduce((pre, curr) => Math.max(pre, curr.length), 0)
);
</script>

<style scoped lang="scss">
@use "@/assets/_vars.scss" as *;
@use "@/assets/_breakpoints.scss" as breakpoints;
</style>
